import type { Action } from 'redux';

import type { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import {
  type AuthSystemDomainSchemaWithIdNullable,
  httpGetSystemDomainsBasicBySystemDomainId,
} from '@iconik-io/iconik-api/src/services/auth';
import { httpGetSystemCurrent } from '@iconik-io/iconik-api/src/services/settings';
import {
  httpGetBillingStatus,
  type StatsBillingStatsSchema,
} from '@iconik-io/iconik-api/src/services/stats';
import {
  httpGetUsersCurrent,
  httpGetUsersCurrentRoles,
} from '@iconik-io/iconik-api/src/services/users';
import { handleErrors } from '@iconik-io/shared-lib/src/utils/handle_messages';
import { isRole } from '@iconik-io/shared-lib/src/utils/roles';

import {
  ASSET_PANELS_KEYS,
  LS_ASSETS_DEFAULT_PANELS,
  SEARCH_PANELS_KEYS,
} from '../../constants/data';
import { emptyUntilLoaded } from '../../store/empty_slice_state';
import type { AppDispatch, GetState } from '../../store/types';
import { setOriginalUserId } from '../auth/slice';
import { errorMessage } from '../messages/thunks';
import {
  getMergedSettings,
  setAssetPanels,
  setSearchPanels,
} from '../settings/thunks';
import {
  setDifferentSystemDomain,
  setDifferentUser,
  setSystemStatus,
} from './slice';
import type { AssetSection, SearchSection } from './types';

export function isSwitchedUser(): string | null {
  const userId = localStorage.getItem('actAsUserId');
  const userName = localStorage.getItem('actAsUserName');
  return userId && userName;
}

function initDifferentUser() {
  return (dispatch: AppDispatch): void => {
    const userId = localStorage.getItem('actAsUserId');
    const userName = localStorage.getItem('actAsUserName');
    if (userId && userName) {
      dispatch(
        setDifferentUser({
          id: userId,
          name: userName,
        }),
      );
    }
  };
}

export function isSwitchedSystemDomain(): string | null {
  const domainId = localStorage.getItem('domainId');
  const domainName = localStorage.getItem('domainName');
  return domainId && domainName;
}

const SYSTEM_DOMAIN_HEADER = 'System-Domain-ID';

export const switchSystemDomain =
  (domain: AuthSystemDomainSchemaWithIdNullable) =>
  (dispatch: AppDispatch): void => {
    const { id, name, base_url } = domain;
    const requestHeaders = {
      [SYSTEM_DOMAIN_HEADER]: id,
    };

    dispatch(httpGetSystemCurrent(undefined, requestHeaders))
      .then(() => {
        dispatch(
          setDifferentSystemDomain({
            id,
            name,
            base_url,
          }),
        );
        window.location.reload();
      })
      .catch((error: EndpointError) => {
        let text: string | string[] = handleErrors(error);
        if (error.response?.status === 403) {
          text = `Support Access is disabled for ${name} system domain`;
        }
        dispatch(errorMessage({ text }));
      });
  };

function initDifferentSystemDomain() {
  return (dispatch: AppDispatch): void => {
    const domainId = localStorage.getItem('domainId');
    const domainName = localStorage.getItem('domainName');
    if (domainId && domainName) {
      dispatch(
        setDifferentSystemDomain({
          id: domainId,
          name: domainName,
        }),
      );
    }
  };
}

export function syncActiveAssetPanel(key: AssetSection, value: boolean) {
  return (dispatch: AppDispatch): Action | boolean => {
    if (ASSET_PANELS_KEYS.includes(key)) {
      let assetPanels: string[] = JSON.parse(
        localStorage.getItem(LS_ASSETS_DEFAULT_PANELS) ?? '[]',
      );
      if (!assetPanels.includes(key) && value) {
        assetPanels.push(key);
      }
      if (assetPanels.includes(key) && !value) {
        assetPanels = assetPanels.filter((item) => item !== key);
      }
      return dispatch(setAssetPanels(assetPanels));
    }

    return false;
  };
}

export function syncSearchPanel(key: SearchSection, value: boolean) {
  return (dispatch: AppDispatch, getState: GetState): Action | boolean => {
    if (SEARCH_PANELS_KEYS.includes(key)) {
      const { searchDefaultSections } = getState().appReducer;
      const updatedSearchPanels = value
        ? searchDefaultSections.concat(key)
        : searchDefaultSections.filter((section) => section !== key);

      return dispatch(setSearchPanels(updatedSearchPanels));
    }

    return false;
  };
}

export function getSystemStatus() {
  return (dispatch: AppDispatch) =>
    dispatch(httpGetBillingStatus())
      .then((response) => {
        dispatch(setSystemStatus(response.body));
      })
      .catch(() => {
        dispatch(setSystemStatus(emptyUntilLoaded<StatsBillingStatsSchema>()));
      });
}

/**
 * The roles initialization order is important in case of switched credentials.
 * There are 3 cases:
 * 1. Switched User
 *    User → Roles
 * 2. Switched System Domain & User
 *    System Domain → User → Roles
 * 3. Switched System Domain
 *    Roles → System Domain
 */
export function initApp() {
  return (dispatch: AppDispatch, getState: GetState) => {
    if (isSwitchedUser()) {
      if (isSwitchedSystemDomain()) {
        dispatch(initDifferentSystemDomain());
      }
      dispatch(initDifferentUser());
    }

    const requests = [
      dispatch(httpGetUsersCurrentRoles()),
      dispatch(httpGetUsersCurrent()).then(async (userResponse) => {
        const domainId = isSwitchedSystemDomain()
          ? getState().appReducer.differentSystemDomain?.id
          : userResponse.body.system_domain_id;
        if (domainId) {
          await dispatch(httpGetSystemDomainsBasicBySystemDomainId(domainId));
        }
        await dispatch(getMergedSettings());

        return userResponse;
      }),
    ] as const;

    if (!isSwitchedUser() && isSwitchedSystemDomain()) {
      dispatch(initDifferentSystemDomain());
    }

    if (isSwitchedUser()) {
      // This is to keep track of the original user that made the switch. See authReducer.originalUserId for some more info
      dispatch(
        httpGetUsersCurrent({
          'User-ID': undefined,
          'System-Domain-ID': undefined,
        }),
      ).then((response) => {
        dispatch(setOriginalUserId(response.body.id));
      });
    }

    dispatch(getSystemStatus());

    return Promise.all(requests).then(([rolesResponse, userResponse]) => ({
      roles: (rolesResponse.body.objects ?? []).filter(isRole),
      user: userResponse.body || {},
    }));
  };
}

export function resetAssetPanelsToSettings() {
  return (dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();
    const settings = state.settings.merged;
    if (settings.asset_default_sections) {
      dispatch(setAssetPanels(settings.asset_default_sections));
    }
  };
}

export function resetSearchPanelsToSettings() {
  return (dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();
    const settings = state.settings.merged;
    if (settings.search_default_sections) {
      dispatch(setSearchPanels(settings.search_default_sections));
    }
  };
}

export function resetPanelsToSettings() {
  return (dispatch: AppDispatch): void => {
    dispatch(resetAssetPanelsToSettings());
    dispatch(resetSearchPanelsToSettings());
  };
}
